<template>
  <div
    v-if="visible || specVisible"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="handleBackdropClick"
  >
    <div
      class="bg-white rounded-2xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto"
      @click.stop
    >
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-xl font-semibold text-gray-900">选择租赁设备</h3>
          <button
            class="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            @click="handleClose"
          >
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
      <div class="p-6">
        <!-- 设备分类标签 -->
        <div class="flex flex-wrap gap-2 mb-6">
          <button
            v-for="category in categories"
            :key="category.key"
            :class="[
              'category-filter px-4 py-2 rounded-full text-sm cursor-pointer hover:bg-gray-200',
              selectedCategory === category.key
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600',
            ]"
            @click="filterByCategory(category.key)"
          >
            {{ category.name }}
          </button>
        </div>
        <!-- 加载状态 -->
        <div v-if="loading" class="flex justify-center items-center py-16">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"
            ></div>
            <p class="mt-3 text-gray-600">加载中...</p>
          </div>
        </div>
        <!-- 设备网格 -->
        <div
          v-else-if="filteredItems.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="relative item-card bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
          >
            <img
              :src="item.thumbnail_url"
              :alt="item.name"
              class="w-full h-32 object-cover object-top rounded-lg mb-3"
            />
            <!-- 角标 -->
            <span
              class="absolute top-4 left-4 bg-primary text-white px-2 py-1 text-xs rounded"
              >{{ item.category_name }}</span
            >
            <h4 class="font-medium text-gray-900 mb-1">{{ item.name }}</h4>
            <p class="text-sm text-gray-500 mb-2">
              {{ item.short_description }}
            </p>
            <div class="flex items-center justify-between">
              <span class="text-primary font-semibold"
                >¥{{ item.price }}/天</span
              >
              <button
                class="add-to-cart px-3 py-1 bg-primary text-white rounded-md text-xs hover:bg-secondary transition-colors cursor-pointer"
                @click="handleAddItem(item)"
              >
                添加
              </button>
            </div>
          </div>
        </div>
        <!-- 空状态 -->
        <div v-else class="flex justify-center items-center py-16">
          <div class="text-center">
            <i class="ri-file-search-line text-5xl text-gray-300 mb-4"></i>
            <p class="text-gray-500">暂无设备数据</p>
          </div>
        </div>

        <!-- 分页组件 -->
        <div class="mt-8">
          <Pagination
            :total-count="totalCount"
            :page_current="page_current"
            :page_size="page_size"
            :visible-range="2"
            @page-change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <!-- 规格选择弹窗 -->
    <div
      v-if="specVisible"
      class="bg-white rounded-2xl max-w-2xl w-50 mx-4 max-h-screen overflow-y-auto"
      @click.stop
    >
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-xl font-semibold text-gray-900">选择规格</h3>
          <button
            class="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            @click="handleSpecClose"
          >
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
      <div class="p-6">
        <div v-if="selectedItem" class="space-y-6">
          <!-- 商品信息 -->
          <div class="flex gap-4">
            <img
              :src="selectedItem.thumbnail_url"
              :alt="selectedItem.name"
              class="w-20 h-20 object-cover rounded-lg"
            />
            <div class="flex-1">
              <h4 class="font-medium text-lg mb-2">{{ selectedItem.name }}</h4>
              <p class="text-sm text-gray-500 mb-3">
                {{ selectedItem.short_description }}
              </p>
              <div class="text-xl font-semibold text-primary">
                ¥{{ currentSpecPrice }}/天
              </div>
            </div>
          </div>

          <!-- 规格选择 -->
          <div class="space-y-4">
            <div v-for="spec in itemSpecs" :key="spec.name" class="mb-4">
              <div class="flex flex-wrap items-center">
                <span class="font-medium text-gray-900 mr-3 whitespace-nowrap"
                  >{{ spec.name }}：</span
                >
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    v-for="value in spec.values"
                    :key="value"
                    class="px-3 py-1 rounded-full text-md cursor-pointer hover:bg-primary hover:text-white transition-colors"
                    :class="{
                      'bg-primary text-white':
                        selectedSpec[spec.name] === value,
                      'bg-gray-100 text-gray-600':
                        selectedSpec[spec.name] !== value,
                    }"
                    @click="handleSpecChange(spec.name, value)"
                  >
                    {{ value }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 数量选择 -->
          <div class="flex items-center">
            <span class="text-gray-700 mr-3">数量：</span>
            <div class="flex items-center border rounded-md">
              <button
                @click="decreaseSpecQuantity"
                class="px-3 py-1 border-r text-gray-500 hover:text-gray-700"
                :disabled="specQuantity <= 1"
              >
                -
              </button>
              <input
                v-model.number="specQuantity"
                type="number"
                class="w-12 text-center border-none outline-none dealnumber"
                :min="1"
              />
              <button
                @click="increaseSpecQuantity"
                class="px-3 py-1 border-l text-gray-500 hover:text-gray-700"
              >
                +
              </button>
            </div>
          </div>

          <!-- 确认按钮 -->
          <div class="flex justify-end mt-6">
            <button
              @click="handleSpecConfirm"
              class="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              :disabled="!isSpecValid"
            >
              确认添加
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/utils/api.js";
import Pagination from "@/components/Pagination.vue";

export default {
  components: {
    Pagination,
  },
  name: "GoodsSelector",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    onAddItem: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      selectedCategory: "all",
      categories: [{ key: "all", name: "全部" }],
      equipmentItems: [],
      loading: false,
      // 分页相关数据
      page_current: 1,
      page_size: 6, // 每页显示12个商品
      totalCount: 0,

      // 规格选择相关
      specVisible: false, // 规格选择弹窗可见性
      selectedItem: null, // 当前选中的商品
      itemSpecs: [], // 当前商品的规格数据
      selectedSpec: {}, // 选中的规格
      specQuantity: 1, // 选择的数量
      skuList: [], // SKU数据列表
      currentSpecPrice: 0, // 当前规格的价格
    };
  },
  computed: {
    filteredItems() {
      if (this.selectedCategory === "all") {
        return this.equipmentItems;
      }
      return this.equipmentItems;
    },

    // 检查规格是否选择完整
    isSpecValid() {
      // 检查每个规格是否都已选择
      for (const spec of this.itemSpecs) {
        if (!this.selectedSpec[spec.name]) {
          return false;
        }
      }
      return true;
    },
  },
  mounted() {
    if (this.visible) {
      this.getCategoryList();
      this.getEquipmentList();
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.categories.length === 1) {
        // 当弹窗可见且分类列表为空时，重新获取数据
        this.getCategoryList();
        this.getEquipmentList();
      }
    },
  },
  methods: {
    // 获取设备分类列表
    async getCategoryList() {
      try {
        this.loading = true;
        const response = await api.goods.getCategoryList();

        // 处理分类数据，确保结构与前端期望一致
        if (response && response.data && response.data.categoryList) {
          const categoryList = response.data.categoryList.map((category) => ({
            key: category.id,
            name: category.name,
          }));
          // 添加"全部"分类到列表开头
          this.categories = [{ key: "all", name: "全部" }, ...categoryList];
        }
      } catch (error) {
        console.error("获取设备分类失败:", error);
        // 保持默认分类列表
      } finally {
        this.loading = false;
      }
    },

    // 获取设备列表
    async getEquipmentList() {
      try {
        this.loading = true;

        // 构建请求参数，与Rent.vue保持一致
        const params = {
          page_current: this.page_current,
          page_size: this.page_size,
          sortBy: "default",
        };

        const response = await api.goods.getList(params);

        // 处理设备列表数据
        if (response && response.data && response.data.resdata) {
          this.equipmentItems = response.data.resdata.map((item) => ({
            id: item.id,
            name: item.name,
            category_name: item.category_name || "",
            short_description: item.short_description || "",
            price: parseFloat(item.price) || 0,
            category: item.category_id,
            thumbnail_url: item.thumbnail_url || "",
            is_models: item.is_models,
            sku_id: item.sku_id,
            supplier_name: item.supplier_name,
            supplier_id: item.supplier_id,
            spec_name: item.spec_name || "",
            spec_text: item.spec_text || "",
          }));
          // 设置总条数，与Rent.vue保持一致使用restotal
          this.totalCount = response.data.restotal || 0;
        }
      } catch (error) {
        console.error("获取设备列表失败:", error);
        this.equipmentItems = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    // 按分类筛选
    filterByCategory(category) {
      this.selectedCategory = category;
      // 当切换分类时，根据所选分类重新获取设备数据
      this.getEquipmentListByCategory(category);
    },

    // 根据分类获取设备列表
    async getEquipmentListByCategory(category) {
      try {
        this.loading = true;

        // 构建请求参数
        const params = {
          page_current: this.page_current,
          page_size: this.page_size,
          sortBy: "default",
        };

        // 如果不是"全部"分类，则添加分类筛选条件
        if (category !== "all") {
          params.categoryIds = category; //数字转字符串
        }

        const response = await api.goods.getList(params);

        // 处理设备列表数据
        if (response && response.data && response.data.resdata) {
          this.equipmentItems = response.data.resdata.map((item) => ({
            id: item.id,
            name: item.name,
            category_name: item.category_name || "",
            short_description: item.short_description || "",
            price: parseFloat(item.price) || 0,
            category: item.category_id,
            thumbnail_url: item.thumbnail_url || "",
            is_models: item.is_models,
            sku_id: item.sku_id,
            supplier_name: item.supplier_name,
            supplier_id: item.supplier_id,
            spec_text: item.spec_text || "",
          }));

          console.log(this.equipmentItems);

          // 设置总条数，与Rent.vue保持一致使用restotal
          this.totalCount = response.data.restotal || 0;
        } else {
          this.equipmentItems = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("根据分类获取设备列表失败:", error);
        this.equipmentItems = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    // 添加商品
    async handleAddItem(item) {
      // 如果是单规格商品，直接添加到购物车
      if (item.is_models === 2) {
        // 添加商品到购物车
        this.onAddItem({
          name: item.name,
          short_description: item.short_description,
          price: item.price,
          category_name: item.category_name || "",
          category: item.category,
          thumbnail_url: item.thumbnail_url,
          // is_models: item.is_models,
          sku_id: item.sku_id,
          quantity: 1,
          supplier_name: item.supplier_name,
          supplier_id: item.supplier_id,
          spec_text: item.spec_text || "",
        });
        this.handleClose();
      } else {
        // 多规格商品，显示规格选择弹窗
        this.selectedItem = item;
        this.specQuantity = 1;

        // 获取商品详情和规格信息
        await this.getItemSpecs(item.id);

        // 显示规格选择弹窗
        this.specVisible = true;
      }
    },

    // 获取商品规格信息
    async getItemSpecs(goodsId) {
      try {
        const response = await api.goods.getDetail({ id: goodsId });
        const goodsDetail = response.data;

        // 初始化规格数据
        this.itemSpecs = goodsDetail.spec || [];
        this.selectedSpec = {};
        this.currentSpecPrice = parseFloat(goodsDetail.price) || 0;

        // 为每个规格选择第一个值作为默认值
        this.itemSpecs.forEach((spec) => {
          if (spec.values && spec.values.length > 0) {
            this.selectedSpec[spec.name] = spec.values[0];
          }
        });

        // 初始化SKU数据，如果没有则生成模拟数据
        this.skuList = goodsDetail.skus || this.generateMockSkuList();

        // 根据默认选择的规格查找对应的SKU
        this.findCurrentSku();
      } catch (error) {
        console.error("获取商品规格信息失败:", error);
        // 初始化空的规格数据
        this.itemSpecs = [];
        this.selectedSpec = {};
        this.skuList = [];
      }
    },

    // 生成模拟SKU数据
    generateMockSkuList() {
      const mockSkus = [];
      // 根据规格组合生成模拟SKU数据
      if (this.itemSpecs.length > 0) {
        const generateSkus = (specs, index = 0, currentSpec = {}) => {
          if (index >= specs.length) {
            // 为每个SKU组合生成不同的价格
            const basePrice = parseFloat(this.selectedItem.price) || 100;
            const priceFactor =
              1 + Object.values(currentSpec).join("").length * 0.1;
            const price = (basePrice * priceFactor).toFixed(2);
            const skuModel = JSON.stringify(currentSpec);

            mockSkus.push({
              id: `sku_${Date.now()}_${mockSkus.length}`,
              goods_id: this.selectedItem.id,
              price: price,
              stock: 100,
              sku_model: skuModel,
            });
            return;
          }

          const spec = specs[index];
          spec.values.forEach((value) => {
            currentSpec[spec.name] = value;
            generateSkus(specs, index + 1, { ...currentSpec });
          });
        };

        generateSkus(this.itemSpecs);
      }
      return mockSkus;
    },

    // 根据当前选中的规格查找对应的SKU
    findCurrentSku() {
      if (this.skuList.length === 0) {
        this.currentSpecPrice = parseFloat(this.selectedItem.price) || 0;
        return;
      }

      // 遍历SKU列表，查找匹配当前规格的SKU
      for (const sku of this.skuList) {
        try {
          const skuSpec = JSON.parse(sku.sku_model);
          let isMatch = true;

          // 检查每个规格是否匹配
          for (const [key, value] of Object.entries(this.selectedSpec)) {
            if (skuSpec[key] !== value) {
              isMatch = false;
              break;
            }
          }

          if (isMatch) {
            this.currentSpecPrice = parseFloat(sku.price) || 0;
            return;
          }
        } catch (e) {
          console.error("解析SKU规格失败:", e);
        }
      }

      // 如果没有找到匹配的SKU，使用商品的基础价格
      this.currentSpecPrice = parseFloat(this.selectedItem.price) || 0;
    },

    // 处理规格选择变化
    handleSpecChange(specName, specValue) {
      this.selectedSpec[specName] = specValue;
      this.findCurrentSku(); // 更新当前SKU和价格
    },

    // 减少数量
    decreaseSpecQuantity() {
      if (this.specQuantity > 1) {
        this.specQuantity--;
      }
    },

    // 增加数量
    increaseSpecQuantity() {
      this.specQuantity++;
    },

    // 确认规格选择并添加商品
    handleSpecConfirm() {
      // 检查是否选择了所有规格
      if (!this.isSpecValid) {
        alert("请选择所有规格");
        return;
      }

      // 构建规格文本
      let specText = "";
      if (this.itemSpecs.length > 0) {
        specText = this.itemSpecs
          .map((spec) => `${spec.name}: ${this.selectedSpec[spec.name]}`)
          .join(", ");
      }

      // 查找当前选中的SKU ID
      let selectedSkuId = this.selectedItem.sku_id;
      for (const sku of this.skuList) {
        try {
          const skuSpec = JSON.parse(sku.sku_model);
          let isMatch = true;
          for (const [key, value] of Object.entries(this.selectedSpec)) {
            if (skuSpec[key] !== value) {
              isMatch = false;
              break;
            }
          }
          if (isMatch) {
            selectedSkuId = sku.id;
            break;
          }
        } catch (e) {
          console.error("解析SKU规格失败:", e);
        }
      }

      // 添加商品到购物车
      this.onAddItem({
        name: this.selectedItem.name,
        short_description: this.selectedItem.short_description,
        price: this.currentSpecPrice,
        category_name: this.selectedItem.category_name || "",
        category: this.selectedItem.category,
        thumbnail_url: this.selectedItem.thumbnail_url,
        sku_id: selectedSkuId,
        quantity: this.specQuantity,
        supplier_name: this.selectedItem.supplier_name,
        supplier_id: this.selectedItem.supplier_id,
        spec_text: specText,
        spec: this.selectedSpec,

      });

      // 关闭规格选择弹窗
      this.handleSpecClose();
    },

    // 关闭规格选择弹窗
    handleSpecClose() {
      this.specVisible = false;
      this.selectedItem = null;
      this.itemSpecs = [];
      this.selectedSpec = {};
      this.specQuantity = 1;
      this.skuList = [];
    },

    // 处理分页变更
    handlePageChange(page) {
      this.page_current = page;
      // 根据当前选中的分类重新获取数据
      if (this.selectedCategory === "all") {
        this.getEquipmentList();
      } else {
        this.getEquipmentListByCategory(this.selectedCategory);
      }
    },

    // 关闭弹窗
    handleClose() {
      // 重置分页状态
      this.page_current = 1;
      this.onClose();
    },

    // 点击背景关闭弹窗
    handleBackdropClick() {
      if (this.specVisible) {
        this.handleSpecClose();
      } else {
        this.handleClose();
      }
    },
  },
};
</script>

<style scoped>
/* 组件样式已经内联在template的class中 */
.dealnumber::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.dealnumber::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
</style>



// WEBPACK FOOTER //
// src/components/GoodsSelector.vue